import { useEffect, useState } from "react";
import { Package, CheckCircle2, XCircle, AlertTriangle, Boxes, Plus, ArrowDownToLine, ArrowUpFromLine, ShoppingCart, FileText } from "lucide-react";
import api, { brl, fmtDate } from "./api";

const Stat = ({ icon: Icon, label, value, tone = "text-military-dark" }) => (
  <div className="bg-white border border-military/10 rounded-lg p-5">
    <Icon size={18} className="text-military mb-3" />
    <p className={`font-serif text-3xl ${tone}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-[0.2em] text-military-dark/50 font-semibold mt-1">{label}</p>
  </div>
);

const typeLabel = { entrada: "Entrada", saida: "Saída", ajuste: "Ajuste", venda: "Venda" };

export default function Dashboard({ go }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <p className="text-military-dark/50 text-sm">Carregando...</p>;

  const shortcuts = [
    { label: "Adicionar produto", icon: Plus, tab: "produtos" },
    { label: "Ver estoque", icon: Boxes, tab: "estoque" },
    { label: "Registrar entrada", icon: ArrowDownToLine, tab: "estoque" },
    { label: "Registrar saída", icon: ArrowUpFromLine, tab: "estoque" },
    { label: "Ver pedidos", icon: ShoppingCart, tab: "pedidos" },
    { label: "Editar site", icon: FileText, tab: "conteudo" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl md:text-4xl text-military-dark mb-8">Visão geral</h1>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <Stat icon={Package} label="Produtos" value={stats.total_products} />
        <Stat icon={CheckCircle2} label="Ativos" value={stats.active_products} />
        <Stat icon={XCircle} label="Esgotados" value={stats.out_of_stock} tone={stats.out_of_stock ? "text-red-700" : "text-military-dark"} />
        <Stat icon={AlertTriangle} label="Estoque baixo" value={stats.low_stock.length} tone={stats.low_stock.length ? "text-amber-700" : "text-military-dark"} />
        <Stat icon={Boxes} label="Unidades em estoque" value={stats.total_units} />
      </div>

      {stats.low_stock.length > 0 && (
        <div className="bg-baby border border-military/15 rounded-lg p-5 mb-10" data-testid="low-stock-alerts">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark mb-3 flex items-center gap-2"><AlertTriangle size={14} /> Alertas de estoque</p>
          {stats.low_stock.map((p) => (
            <p key={p.slug} className="text-sm text-military-dark/80" data-testid={`alert-${p.slug}`}>
              Estoque baixo: <strong>{p.name}</strong> — restam {p.stock} unidades (mínimo: {p.min_stock}).
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-12">
        {shortcuts.map((s) => (
          <button
            key={s.label}
            data-testid={`shortcut-${s.label.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => go(s.tab)}
            className="flex items-center gap-3 bg-military-dark text-offwhite rounded-lg px-5 py-4 text-xs uppercase tracking-widest font-semibold hover:bg-military transition-colors"
          >
            <s.icon size={16} /> {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white border border-military/10 rounded-lg p-6">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-4">Pedidos recentes</p>
          {stats.recent_orders.length === 0 && <p className="text-sm text-military-dark/40">Nenhum pedido ainda.</p>}
          {stats.recent_orders.map((o) => (
            <div key={o.id} className="flex justify-between items-center border-b border-military/10 last:border-0 py-3">
              <div>
                <p className="text-sm font-semibold text-military-dark">{o.code} <span className="text-[10px] uppercase tracking-wider text-military-dark/40 ml-1">{o.origin === "whatsapp" ? "whatsapp" : "site"}</span></p>
                <p className="text-xs text-military-dark/50">{o.customer?.name} · {fmtDate(o.created_at)}</p>
              </div>
              <p className="text-sm font-semibold text-military-dark">{brl(o.total)}</p>
            </div>
          ))}
        </div>
        <div className="bg-white border border-military/10 rounded-lg p-6">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-4">Últimas movimentações</p>
          {stats.recent_movements.length === 0 && <p className="text-sm text-military-dark/40">Nenhuma movimentação ainda.</p>}
          {stats.recent_movements.map((m) => (
            <div key={m.id} className="flex justify-between items-center border-b border-military/10 last:border-0 py-3">
              <div>
                <p className="text-sm font-semibold text-military-dark">{m.product_name}</p>
                <p className="text-xs text-military-dark/50">{typeLabel[m.type] || m.type} · {m.user} · {fmtDate(m.created_at)}</p>
              </div>
              <p className={`text-sm font-semibold ${m.type === "entrada" || m.type === "ajuste" ? "text-military" : "text-red-700"}`}>
                {m.type === "entrada" ? "+" : m.type === "ajuste" ? "=" : "-"}{m.qty}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
